import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Alert, Button, Col, Form, Input, message, Row, Space } from 'antd'
import { EditOutlined } from '@ant-design/icons'
import UserService from '../service/UserService'
import WallexService from '../service/WallexService'
import User, { UserRetrieve } from '../domain/User'
import { statusLabel } from '../domain/Status'
import useEntity from '../hooks/useEntity'
import { paths } from '../routes'
import FindBox from './ui/FindBox'
import EntityState from './ui/EntityState'
import EntitySummary from './ui/EntitySummary'
import StatusChanger from './ui/StatusChanger'

interface NameValues {
    firstName: string
    lastName: string
}

const fullName = (user: UserRetrieve) => [user.firstName, user.lastName].filter(Boolean).join(' ')
const hasName = (user: UserRetrieve) => !!user.firstName && !!user.lastName

interface UserNameEditorProps {
    user: UserRetrieve
    onSave: (values: NameValues) => Promise<unknown>
    onClose: () => void
}

const UserNameEditor: React.FC<UserNameEditorProps> = ({ user, onSave, onClose }) => {
    const [saving, setSaving] = useState<boolean>(false)
    const [error, setError] = useState<string | null>(null)

    const onFinish = ({ firstName, lastName }: NameValues) => {
        setSaving(true)
        setError(null)
        onSave({ firstName: firstName.trim(), lastName: lastName.trim() })
            .then(onClose)
            .catch((e) => setError(WallexService.errorDescription(e, 'Couldn’t update the name.')))
            .finally(() => setSaving(false))
    }

    return (
        <Form className="inline-editor" layout="vertical" initialValues={{ firstName: user.firstName, lastName: user.lastName }} onFinish={onFinish}>
            <Row gutter={16}>
                <Col xs={24} sm={12}>
                    <Form.Item label="First Name" name="firstName" rules={[{ required: true, whitespace: true, message: 'Please input First Name' }]}>
                        <Input />
                    </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                    <Form.Item label="Last Name" name="lastName" rules={[{ required: true, whitespace: true, message: 'Please input Last Name' }]}>
                        <Input />
                    </Form.Item>
                </Col>
            </Row>
            {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 16 }} />}
            <Space>
                <Button type="primary" htmlType="submit" loading={saving}>
                    Save name
                </Button>
                <Button onClick={onClose}>Cancel</Button>
            </Space>
        </Form>
    )
}

const pendingDocsNotice = (
    <>
        <Alert
            message="User Status"
            description="You can move Pending Documents users to Pending Approval, Rejected, or Withdrawn. Post-approval users can be moved to Suspended, Dormant, Terminated, or Closed based on the account status lifecycle."
            type="warning"
        />
        <Alert
            style={{ marginTop: 8 }}
            message="Before approving user"
            description={
                <>
                    To update the status to Approved, complete the{' '}
                    <a href="https://docs.wallex.asia/docs/api/users/user-detail-update" target="_blank" rel="noopener noreferrer">
                        User Detail Update
                    </a>{' '}
                    and{' '}
                    <a href="https://docs.wallex.asia/docs/api/users/company-update" target="_blank" rel="noopener noreferrer">
                        Company Update
                    </a>{' '}
                    first.
                </>
            }
            type="warning"
        />
    </>
)

const UserForm: React.FC = () => {
    const { id } = useParams()
    const [editingName, setEditingName] = useState<boolean>(false)
    const [messageApi, messageContext] = message.useMessage()
    const {
        entity: user,
        setEntity,
        loading,
        error,
        find,
        refresh,
    } = useEntity<UserRetrieve>({
        kind: 'user',
        id,
        retrieve: (userId) => UserService.retrieve(userId),
        pathFor: paths.user,
        notFound: 'User not found. Check the ID and try again.',
        remember: (found) => ({ label: fullName(found) || undefined, accountId: found.accountId, userId: found.id }),
    })

    // Both names are required by the API, so ask for them straight away when they're missing
    useEffect(() => {
        if (user) setEditingName(!hasName(user))
    }, [user?.id])

    const statuses = user ? User.statuses(user) : []

    // The API takes the status key; statuses the simulator doesn't know are sent as they are
    const statusKey = (status: string) => {
        const definition = statuses.find((each) => each.value === status)
        return definition ? definition.key.toString() : status
    }

    const changeStatus = (status: string, reason?: string) => {
        const current = user as UserRetrieve
        if (!hasName(current)) return Promise.reject(new Error('Add the user’s first and last name first'))

        return UserService.update(current.id, { firstName: current.firstName, lastName: current.lastName, status: statusKey(status), remarks: reason }).then(() =>
            setEntity((latest) => latest && { ...latest, status })
        )
    }

    const saveName = ({ firstName, lastName }: NameValues) => {
        const current = user as UserRetrieve
        return UserService.update(current.id, { firstName, lastName, status: statusKey(current.status) }).then(() => {
            setEntity((latest) => latest && { ...latest, firstName, lastName })
            void messageApi.success('Name updated')
        })
    }

    return (
        <div className="stack">
            {messageContext}
            <FindBox kind="user" title="Find a User" placeholder="Enter User ID" value={id} loading={loading} onFind={find} />
            <EntityState id={id} loading={loading} error={error} ready={!!user} emptyText="Enter a User ID above, or pick a recent one.">
                {user && (
                    <>
                        <EntitySummary
                            title="User Information"
                            status={user.status}
                            statusLabel={statusLabel(user.status, statuses)}
                            items={[
                                { label: 'Name', value: fullName(user) || 'Not set' },
                                { label: 'User ID', copy: user.id },
                                { label: 'Account ID', copy: user.accountId },
                            ]}
                            refreshing={loading}
                            onRefresh={refresh}
                            extra={
                                !editingName && (
                                    <Button size="small" icon={<EditOutlined />} onClick={() => setEditingName(true)}>
                                        Edit name
                                    </Button>
                                )
                            }
                        >
                            {editingName && <UserNameEditor user={user} onSave={saveName} onClose={() => setEditingName(false)} />}
                        </EntitySummary>
                        <StatusChanger
                            noun="user"
                            current={user.status}
                            statuses={statuses}
                            journey={User.journey}
                            hints={User.hints}
                            reasonLabel="Remarks"
                            notice={user.status === 'pending_docs' && pendingDocsNotice}
                            onSubmit={changeStatus}
                        />
                    </>
                )}
            </EntityState>
        </div>
    )
}

export default UserForm
